import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarWidget } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, Pencil, X, Save, CornerDownRight, ClipboardList, CalendarIcon, Lock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { cn, formatDDMMYYYY } from '@/lib/utils';
import api from '../services/api';

/**
 * Findings, Commentary & Actions — standalone management layer.
 *
 * IMPORTANT scope guard: this dialog is reachable ONLY from the new
 * Stocktake module session workflow (StocktakeSessionPage.jsx). It is
 * a NEW, separate data model (`SessionFindingAction`) and does NOT
 * touch, extend, or link to the existing standalone Summary Stock
 * Audit Wizard in any way.
 */

const CATEGORY_OPTIONS = [
  { value: 'stock_control', label: 'Stock Control' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'wastage', label: 'Wastage' },
  { value: 'security', label: 'Security' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'other', label: 'Other' },
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'carried_forward', label: 'Carried Forward' },
];

const PRIORITY_BADGE = { low: 'bg-emerald-100 text-emerald-800 border-emerald-300', medium: 'bg-amber-100 text-amber-800 border-amber-300', high: 'bg-red-100 text-red-800 border-red-300' };
const STATUS_BADGE = { open: 'bg-blue-100 text-blue-800 border-blue-300', completed: 'bg-emerald-100 text-emerald-800 border-emerald-300', carried_forward: 'bg-amber-100 text-amber-800 border-amber-300' };

const EMPTY_FORM = { finding: '', recommended_action: '', category: 'other', priority: 'medium', owner: '', due_date: '', status: 'open', auditor_comment: '', management_response: '' };
// Pre-deployment defect sweep #7 — once the session is locked, only
// these fields remain editable on an EXISTING finding/action.
const LOCKED_EDITABLE_FIELDS = ['status', 'owner', 'due_date', 'management_response'];

export default function FindingsActionsDialog({ open, onOpenChange, sessionId, sessionLocked = false }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [candidates, setCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadItems = useCallback(() => {
    if (!sessionId) return;
    setLoading(true);
    api.get(`/stocktake/findings-actions/sessions/${sessionId}`)
      .then(res => setItems(res.data?.items || []))
      .catch(err => toast({ title: 'Could not load Findings & Actions', description: err?.response?.data?.detail || err.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [sessionId, toast]);

  useEffect(() => {
    if (!open || !sessionId) return;
    loadItems();
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCandidates(null);
  }, [open, sessionId, loadItems]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      finding: item.finding || '', recommended_action: item.recommended_action === '—' ? '' : (item.recommended_action || ''),
      category: item.category || 'other', priority: item.priority || 'medium', owner: item.owner || '',
      due_date: item.due_date || '', status: item.status || 'open', auditor_comment: item.auditor_comment || '',
      management_response: item.management_response || '',
    });
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

  const saveForm = async () => {
    if (!form.finding.trim()) {
      toast({ title: 'Finding is required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      // Pre-deployment defect sweep #7 — on a locked session, only send
      // the fields that remain editable (Status, Owner, Due Date,
      // Management Response). Everything else is display-only in the
      // form below, but we still guard the payload itself here so an
      // unchanged frozen value can never trip the backend's freeze check.
      const payload = (sessionLocked && editingId)
        ? { status: form.status, owner: form.owner, due_date: form.due_date || null, management_response: form.management_response }
        : { ...form, due_date: form.due_date || null };
      if (editingId) {
        await api.patch(`/stocktake/findings-actions/${editingId}`, payload);
        toast({ title: 'Finding updated' });
      } else {
        await api.post(`/stocktake/findings-actions/sessions/${sessionId}`, payload);
        toast({ title: 'Finding added' });
      }
      cancelForm();
      loadItems();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const deleteItem = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/stocktake/findings-actions/${id}`);
      toast({ title: 'Finding removed' });
      loadItems();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const loadCandidates = () => {
    setCandidatesLoading(true);
    api.get(`/stocktake/findings-actions/sessions/${sessionId}/carry-forward-candidates`)
      .then(res => setCandidates(res.data))
      .catch(err => toast({ title: 'Could not check prior audit', description: err?.response?.data?.detail || err.message, variant: 'destructive' }))
      .finally(() => setCandidatesLoading(false));
  };

  const carryForwardAll = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/stocktake/findings-actions/sessions/${sessionId}/carry-forward`, {});
      toast({ title: `${res.data.carried_forward_count} item(s) carried forward` });
      setCandidates(null);
      loadItems();
    } catch (err) {
      toast({ title: 'Carry forward failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="findings-actions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-4 w-4" />Findings, Commentary &amp; Actions</DialogTitle>
          <DialogDescription>Log auditor findings and recommended actions for this session. Separate from the Summary Stock Audit Report.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-2">
          {sessionLocked ? (
            <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="findings-locked-banner">
              <Lock className="h-3 w-3" />Session is locked — Finding, Action, Category, Priority and Auditor Comment are frozen. Status, Owner, Due Date and Management Response can still be edited.
            </p>
          ) : (
            <Button size="sm" variant="outline" onClick={loadCandidates} disabled={candidatesLoading} data-testid="check-carry-forward-btn">
              {candidatesLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CornerDownRight className="h-3 w-3 mr-1" />}
              Carry forward from previous session
            </Button>
          )}
          {!showForm && !sessionLocked && (
            <Button size="sm" onClick={startCreate} data-testid="add-finding-btn">
              <Plus className="h-3 w-3 mr-1" />Add Finding
            </Button>
          )}
        </div>

        {candidates && !sessionLocked && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 mb-2 text-xs" data-testid="carry-forward-panel">
            {!candidates.prior_session ? (
              <p className="text-muted-foreground">No prior session found for this site.</p>
            ) : candidates.candidates.length === 0 ? (
              <p className="text-muted-foreground">No open items in the prior session ({candidates.prior_session.session_reference}) to carry forward.</p>
            ) : (
              <div className="space-y-1.5">
                <p className="font-medium">{candidates.candidates.length} open item(s) from {candidates.prior_session.session_reference} ({formatDDMMYYYY(candidates.prior_session.session_date)}):</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {candidates.candidates.map(c => <li key={c.id}>{c.finding}</li>)}
                </ul>
                <Button size="sm" onClick={carryForwardAll} disabled={busy} data-testid="carry-forward-confirm-btn">
                  {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CornerDownRight className="h-3 w-3 mr-1" />}
                  Carry Forward All
                </Button>
              </div>
            )}
          </div>
        )}

        {showForm && (
          <div className="rounded-md border border-slate-200 p-3 mb-2 space-y-2" data-testid="finding-form">
            {sessionLocked && (
              <p className="text-[10px] text-amber-700 flex items-center gap-1"><Lock className="h-3 w-3" />Finding, Action, Category, Priority and Auditor Comment are frozen on this locked session.</p>
            )}
            <div>
              <Label className="text-xs">Finding / Observation *</Label>
              <Textarea rows={2} value={form.finding} disabled={sessionLocked} onChange={e => setForm(f => ({ ...f, finding: e.target.value }))} data-testid="finding-input" />
            </div>
            <div>
              <Label className="text-xs">Recommended Action</Label>
              <Textarea rows={2} value={form.recommended_action} disabled={sessionLocked} onChange={e => setForm(f => ({ ...f, recommended_action: e.target.value }))} data-testid="finding-action-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} disabled={sessionLocked} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="finding-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} disabled={sessionLocked} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="finding-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Owner / Responsible Person</Label>
                <Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} data-testid="finding-owner-input" />
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn('w-full justify-start text-left font-normal h-9', !form.due_date && 'text-muted-foreground')}
                      data-testid="finding-due-date-input"
                    >
                      <CalendarIcon className="h-3.5 w-3.5 mr-2" />
                      {form.due_date ? formatDDMMYYYY(form.due_date) : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarWidget
                      mode="single"
                      selected={form.due_date ? parseISO(form.due_date) : undefined}
                      onSelect={(d) => setForm(f => ({ ...f, due_date: d ? format(d, 'yyyy-MM-dd') : '' }))}
                      data-testid="finding-due-date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="finding-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Auditor Comment</Label>
              <Textarea rows={2} value={form.auditor_comment} disabled={sessionLocked} onChange={e => setForm(f => ({ ...f, auditor_comment: e.target.value }))} data-testid="finding-comment-input" />
            </div>
            <div>
              <Label className="text-xs">Management Response</Label>
              <Textarea rows={2} value={form.management_response} onChange={e => setForm(f => ({ ...f, management_response: e.target.value }))} data-testid="finding-management-response-input" />
            </div>
            <div className="flex justify-end gap-1.5">
              <Button size="sm" variant="ghost" onClick={cancelForm} data-testid="finding-form-cancel-btn"><X className="h-3 w-3 mr-1" />Cancel</Button>
              <Button size="sm" onClick={saveForm} disabled={busy} data-testid="finding-form-save-btn">
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="findings-empty-state">No findings or actions logged yet for this session.</p>
        ) : (
          <div className="space-y-2" data-testid="findings-list">
            {items.map(item => (
              <div key={item.id} className="rounded-md border border-slate-200 p-2.5" data-testid={`finding-card-${item.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.finding}</p>
                    {item.recommended_action && <p className="text-xs text-muted-foreground mt-0.5">→ {item.recommended_action}</p>}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <Badge className={`text-[9px] ${PRIORITY_BADGE[item.priority] || ''}`}>{PRIORITY_OPTIONS.find(p => p.value === item.priority)?.label || item.priority}</Badge>
                      <Badge className={`text-[9px] ${STATUS_BADGE[item.status] || ''}`}>{STATUS_OPTIONS.find(s => s.value === item.status)?.label || item.status}</Badge>
                      <span className="text-[10px] text-muted-foreground">{CATEGORY_OPTIONS.find(c => c.value === item.category)?.label || item.category}</span>
                      {item.owner && <span className="text-[10px] text-muted-foreground">· {item.owner}</span>}
                      {item.due_date && <span className="text-[10px] text-muted-foreground">· Due {formatDDMMYYYY(item.due_date)}</span>}
                    </div>
                    {item.carried_forward_from_id && (
                      <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`finding-provenance-${item.id}`}>
                        <CornerDownRight className="h-3 w-3 shrink-0" />
                        Carried over from {item.carried_forward_from_session_reference || 'a prior audit'}
                        {item.carried_forward_from_session_date && ` (${formatDDMMYYYY(item.carried_forward_from_session_date)})`}
                        {item.carried_forward_by_name && ` by ${item.carried_forward_by_name}`}
                        {item.carried_forward_at && ` on ${formatDDMMYYYY(item.carried_forward_at)}`}
                      </p>
                    )}
                    {item.auditor_comment && <p className="text-[10px] text-muted-foreground mt-1 italic">"{item.auditor_comment}"</p>}
                    {item.management_response && (
                      <p className="text-[10px] text-slate-700 mt-1 bg-slate-50 rounded px-1.5 py-1" data-testid={`finding-management-response-${item.id}`}>
                        <span className="font-medium">Management Response:</span> {item.management_response}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(item)} data-testid={`finding-edit-btn-${item.id}`}><Pencil className="h-3 w-3" /></Button>
                    {!sessionLocked && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => setDeleteTarget(item)} data-testid={`finding-delete-btn-${item.id}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="finding-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this finding?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.finding}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finding-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteItem(deleteTarget.id)}
              data-testid="finding-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
